import { useState } from "react";
import { Film, Download, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const FORMATS = [
  { id: "vertical", label: "Vertical 9:16", hint: "Reels · Stories · TikTok", ratio: "aspect-[9/16]" },
  { id: "square", label: "Carré 1:1", hint: "Fil Facebook · Instagram", ratio: "aspect-square" },
  { id: "horizontal", label: "Horizontal 16:9", hint: "YouTube · bannière", ratio: "aspect-video" },
];

const SPOT_FORMATS = [
  { id: "horizontal", label: "Horizontal 16:9", hint: "YouTube · site · pub", ratio: "aspect-video" },
  { id: "vertical", label: "Vertical 9:16", hint: "Reels · Stories · TikTok", ratio: "aspect-[9/16]" },
];

const SHORT_FORMATS = [
  { id: "vertical", label: "Vertical 9:16", hint: "Reels · Stories · TikTok", ratio: "aspect-[9/16]" },
  { id: "square", label: "Carré 1:1", hint: "Fil Facebook · Instagram", ratio: "aspect-square" },
  { id: "horizontal", label: "Horizontal 16:9", hint: "YouTube · display", ratio: "aspect-video" },
];

export const PubVideoPanel = () => {
  const [busy, setBusy] = useState(null);
  const [busySpot, setBusySpot] = useState(null);
  const [busyShort, setBusyShort] = useState(null);
  const [busyTraj, setBusyTraj] = useState(null);

  const dl = async (fmt) => {
    setBusy(fmt);
    try {
      const blob = await api.pubVideo(fmt);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `pub-mon-parcours-${fmt}.mp4`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Vidéo téléchargée.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Téléchargement impossible.");
    } finally { setBusy(null); }
  };

  const dlSpot = async (fmt) => {
    setBusySpot(fmt);
    try {
      const blob = await api.spotVideo(fmt);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `spot-mon-projet-protege-${fmt}.mp4`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Spot téléchargé.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Téléchargement impossible.");
    } finally { setBusySpot(null); }
  };

  const dlShort = async (fmt) => {
    setBusyShort(fmt);
    try {
      const blob = await api.spotShort(fmt);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `spot-court-${fmt}.mp4`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Version courte téléchargée.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Téléchargement impossible.");
    } finally { setBusyShort(null); }
  };

  const dlTraj = async (fmt) => {
    setBusyTraj(fmt);
    try {
      const blob = await api.trajectoire(fmt);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `trajectoire-ep1-adresse-${fmt}.mp4`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Épisode téléchargé.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Téléchargement impossible.");
    } finally { setBusyTraj(null); }
  };

  return (
    <div className="mt-8 space-y-10" data-testid="pub-video-panel">
      <div className="space-y-4">
        <div className="flex items-start gap-2 text-sm text-slate-500 max-w-3xl">
          <Sparkles size={16} className="mt-0.5 shrink-0" style={{ color: "#C89A3E" }} />
          <span><strong className="text-[#012141]">Chaîne « Trajectoire » — Épisode 1 : Votre adresse</strong> — mini-documentaire narré (votre voix) sur l'histoire de l'adresse et du code postal, et la leçon pour les immigrants. Identité ambre/encre. Construit votre autorité et votre audience, puis ramène vers votre conseil.</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[{ id: "horizontal", label: "Mini-doc 16:9 (~2 min)", hint: "YouTube · site", ratio: "aspect-video" },
            { id: "vertical", label: "Court 9:16 (~50 s · sous-titré)", hint: "Reels · TikTok · Shorts", ratio: "aspect-[9/16]" }].map((f) => (
            <div key={f.id} className="rounded-3xl border border-[#C89A3E]/40 bg-white p-6 shadow-sm hover:shadow-lg transition-shadow">
              <div className={`${f.ratio} w-full max-h-40 mx-auto rounded-2xl bg-[#12100c] grid place-items-center mb-4`}>
                <Sparkles size={30} style={{ color: "#E0B45A" }} />
              </div>
              <div className="font-heading font-bold text-[#012141]">{f.label}</div>
              <div className="text-xs text-slate-500 mt-0.5">{f.hint}</div>
              <button data-testid={`traj-video-${f.id}`} disabled={busyTraj === f.id} onClick={() => dlTraj(f.id)}
                className="mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-[#012141] text-white font-bold hover:brightness-110 disabled:opacity-60 transition-all">
                {busyTraj === f.id ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />} Télécharger l'épisode
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="space-y-4">
        <div className="flex items-start gap-2 text-sm text-slate-500 max-w-3xl">
          <Sparkles size={16} className="mt-0.5 shrink-0" style={{ color: "#C89A3E" }} />
          <span><strong className="text-[#012141]">Spot premium (mini-film ~2 min)</strong> — un vrai film de marque : séquences cinématographiques animées (mouvement Ken Burns), textes clés, musique progressive et VOTRE voix authentique. Progression émotionnelle puis appel à l'action. Idéal comme publicité principale.</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {SPOT_FORMATS.map((f) => (
            <div key={f.id} className="rounded-3xl border border-[#C89A3E]/40 bg-white p-6 shadow-sm hover:shadow-lg transition-shadow">
              <div className={`${f.ratio} w-full max-h-40 mx-auto rounded-2xl bg-[#012141] grid place-items-center mb-4`}>
                <Sparkles size={30} style={{ color: "#C89A3E" }} />
              </div>
              <div className="font-heading font-bold text-[#012141]">{f.label}</div>
              <div className="text-xs text-slate-500 mt-0.5">{f.hint}</div>
              <button data-testid={`spot-video-${f.id}`} disabled={busySpot === f.id} onClick={() => dlSpot(f.id)}
                className="mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-[#012141] text-white font-bold hover:brightness-110 disabled:opacity-60 transition-all">
                {busySpot === f.id ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />} Télécharger le spot
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="space-y-4">
        <div className="flex items-start gap-2 text-sm text-slate-500 max-w-3xl">
          <Sparkles size={16} className="mt-0.5 shrink-0" style={{ color: "#C89A3E" }} />
          <span><strong className="text-[#012141]">Version courte (~30 s · sous-titrée)</strong> — format publicité payante : accroche → idée clé → appel à l'action, avec <strong>sous-titres intégrés</strong> (lisible sans le son). Idéale pour Facebook, Instagram, TikTok et YouTube. Choisissez le format de votre réseau.</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {SHORT_FORMATS.map((f) => (
            <div key={f.id} className="rounded-3xl border border-[#C89A3E]/40 bg-white p-6 shadow-sm hover:shadow-lg transition-shadow">
              <div className={`${f.ratio} w-full max-h-40 mx-auto rounded-2xl bg-[#012141] grid place-items-center mb-4`}>
                <Sparkles size={28} style={{ color: "#C89A3E" }} />
              </div>
              <div className="font-heading font-bold text-[#012141]">{f.label}</div>
              <div className="text-xs text-slate-500 mt-0.5">{f.hint}</div>
              <button data-testid={`spot-short-${f.id}`} disabled={busyShort === f.id} onClick={() => dlShort(f.id)}
                className="mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-[#012141] text-white font-bold hover:brightness-110 disabled:opacity-60 transition-all">
                {busyShort === f.id ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />} Télécharger la version courte
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="space-y-4">
        <div className="flex items-start gap-2 text-sm text-slate-500 max-w-3xl">
          <Film size={16} className="mt-0.5 shrink-0" style={{ color: "#C89A3E" }} />
          <span>Vidéo courte « Mon parcours » (~60 s), narrée avec <strong>votre voix authentique</strong>, en charte navy/or et sous-titres animés mot par mot. Choisissez le format adapté à votre réseau.</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {FORMATS.map((f) => (
          <div key={f.id} className="rounded-3xl border border-[#012141]/10 bg-white p-6 shadow-sm hover:shadow-lg transition-shadow">
            <div className={`${f.ratio} w-full max-h-40 mx-auto rounded-2xl bg-[#012141] grid place-items-center mb-4`}>
              <Film size={30} style={{ color: "#C89A3E" }} />
            </div>
            <div className="font-heading font-bold text-[#012141]">{f.label}</div>
            <div className="text-xs text-slate-500 mt-0.5">{f.hint}</div>
            <button data-testid={`pub-video-${f.id}`} disabled={busy === f.id} onClick={() => dl(f.id)}
              className="mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-[#C89A3E] text-[#012141] font-bold hover:brightness-95 disabled:opacity-60 transition-all">
              {busy === f.id ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />} Télécharger
            </button>
          </div>
        ))}
      </div>
      </div>
    </div>
  );
};
